import { parseInt, get } from 'lodash';
import { StoryMediaQuery } from 'types/story';
import { prependUrlProtocol } from 'utils/helpers';
import urlHelper from './url-helper';

export function getAssetURL(params: IAssetResource | null | undefined, protocol?: boolean): string {
	if (!params) {
		return '';
	}

	const HOSTING_MAP = {
		'storycards-infra-prod-storygalleryimgixassetsbuck-1hz9d9wcpsh2c':
			'//storycards-infra-prod-storygalleryimgixassetsbuck-1hz9d9wcpsh2c.s3-eu-west-1.amazonaws.com',
		'storycards-infra-prod-storygalleryassetsbucket-p2lxrzag8t3u':
			'//storycards-infra-prod-storygalleryassetsbucket-p2lxrzag8t3u.s3-eu-west-1.amazonaws.com',
		'storycards-story-gallery-assets': '//storycards-story-gallery-assets.s3-eu-west-1.amazonaws.com',
		'storycards-story-gallery-imgix': '//storycards-story-gallery-imgix.s3-eu-west-1.amazonaws.com',
		// prod image
		'prod-storycards-gallery-imgix': '//ix.stories.sc',
		// prod video, font
		'prod-storycards-gallery-assets': '//assts.stories.sc',

		// dev image
		'dev-low-cost-galleryimgixbucket349e0b82-cab4l3150sim': '//dev-storycards.imgix.net',
		// dev video, font
		'dev-low-cost-galleryassetsbucket9eb44ac2-pkzruw7z30pc':
			'//dev-low-cost-galleryassetsbucket9eb44ac2-pkzruw7z30pc.s3.eu-west-1.amazonaws.com',
		default: urlHelper.adminBackend,
	};
	const hosting = get(HOSTING_MAP, get(params, 'hosting'), HOSTING_MAP.default);

	return `${protocol ? 'https:' : ''}${hosting}/${get(params, 'filepath')}`;
}

export type FitSize = 'cover' | 'contain';

export const BG_PARAMS = {
	// Custom: original image width {number}
	oW: 'oW',
	// Custom: original width height {number}
	oH: 'oH',
	// Custom: how image resized to fit its container: FitSize
	fsz: 'fsz',
	// Imgix rect param
	rect: 'rect',
};

// Only for a not cropped (without "rect" param) sources passed to background-image
export const IMGIX_PARAMS = {
	DESKTOP: 'fit=fillmax&w=1800&q=80&auto=format,compress',
	MOBILE: 'fit=fillmax&w=750&q=80&auto=format,compress',
	TOOL_ICON: 'q=80&w=64',
};

export const PICTURES_FILE_TYPES = ['.gif', '.jpg', '.jpeg', '.tiff', '.png', '.svg', '.webp'];

export const VIDEO_FILE_TYPES = ['.mp4', '.webm'];

export const bgImage = (url: string) => ({ backgroundImage: `url('${url}')` });

export const isImgix = (url: string = '') => /imgix.net|ix.stories.sc/i.test(url);

export const isGif = (url: string = '') => /.gif/i.test(url);

export const isSvg = (url: string = '') => /.svg/i.test(url);

export const getSourceType = (src: string = '') => {
	switch (true) {
		case /vimeo.com/i.test(src):
			return 'vimeo';
		case /(youtu.be\/)|(youtube.com\/)/i.test(src):
			return 'youtube';
		case RegExp(VIDEO_FILE_TYPES.join('|'), 'i').test(src):
			return 'video';
		case RegExp(PICTURES_FILE_TYPES.join('|'), 'i').test(src):
			return 'image';
		case /data:/.test(src):
			return 'uri';
		default:
			return 'none';
	}
};

export function getOptimizedSrc(
	props:
		| { src: string; mq: StoryMediaQuery['config']; width?: undefined }
		| { src: string; width: number; mq?: undefined }
) {
	const src = cutURLMethod(props.src);
	const is = { svg: isSvg(src), gif: isGif(src), imgix: isImgix(src), none: src === 'none' };

	// drop search params (do not optimize gif|svg)
	if (is.imgix && (is.svg || is.gif)) {
		try {
			const url = new URL(src);
			return `${url.origin}${url.pathname}`;
		} catch (e) {
			return src;
		}
	}

	// Only for a not cropped (without "rect" param) sources passed to background-image
	if (props.mq) {
		let result = src;

		if (is.imgix && !is.gif && !is.svg && !is.none) {
			const hasParams = src.match(/\?/);
			const mobileSize = props.mq.mobile.maxWidth ?? 0;

			const params = window.innerWidth <= mobileSize ? IMGIX_PARAMS.MOBILE : IMGIX_PARAMS.DESKTOP;
			result = `${src}${hasParams ? '&' : '?'}${params}`;
		}

		return result;
	}

	try {
		// Only for images cropped via "rect" param by imgix
		const url = new URL(src);
		const oW = +(url.searchParams.get(BG_PARAMS.oW) ?? '0');
		const w = Math.min(oW || 1920 * 2, Math.round(props.width * window.devicePixelRatio));
		if (w > 0) {
			url.searchParams.set('w', `${w}`);
			url.searchParams.set('q', '80');
			url.searchParams.set('auto', 'format,compress');
			return decodeURIComponent(url.href);
		}
		return props.src;
	} catch (e) {
		return props.src;
	}
}

export function cutURLMethod(str: string) {
	if (str.substr(0, 4) === 'url(') {
		return str.substr(4, str.length - 5);
	}
	return str;
}

export function parseImgSrc(
	srcParam: string = '',
	{ backgroundSize: bgSize, backgroundPosition: bgPos }: { backgroundSize?: string; backgroundPosition?: string } = {}
) {
	try {
		let src = cutURLMethod(srcParam);

		if (src === 'none' || !src) {
			throw new Error('no source');
		}

		src = prependUrlProtocol(src, 'https');

		const url = new URL(decodeURIComponent(src));

		// inject properties into url
		if (isImgix(src)) {
			if (bgSize) url.searchParams.set(BG_PARAMS.fsz, bgSize);
			if (bgPos) url.searchParams.set(BG_PARAMS.rect, bgPos.replace('rect=', ''));
		}

		return {
			url,
			href: decodeURIComponent(url.href),
			rect: url.searchParams.get(BG_PARAMS.rect),
			fsz: (url.searchParams.get(BG_PARAMS.fsz) as FitSize | null) ?? undefined,
			originalWidth: parseInt(url.searchParams.get(BG_PARAMS.oW) ?? '0'),
			originalHeight: parseInt(url.searchParams.get(BG_PARAMS.oH) ?? '0'),
		};
	} catch (e) {
		return { href: '', rect: '', originalWidth: 0, originalHeight: 0, url: null };
	}
}

// get default imgix rect param to crop an image
export function getDefaultRect(
	imageSize: { width: number; height: number },
	bbox: Pick<DOMRect, 'width' | 'height'>,
	fit: FitSize = 'cover'
) {
	let resizedW = 0;
	let resizedH = 0;

	// get ratio to fill (cover) image into bbox
	const rx = bbox.width / imageSize.width;
	const ry = bbox.height / imageSize.height;
	const r = Math[fit === 'cover' ? 'max' : 'min'](rx, ry);

	if (r === ry) {
		// portrait
		resizedH = bbox.height;
		resizedW = imageSize.width * r;
	}
	if (r === rx) {
		// landscape
		resizedW = bbox.width;
		resizedH = imageSize.height * r;
	}

	const offsetX = (resizedW - bbox.width) / 2;
	const offsetY = (resizedH - bbox.height) / 2;

	const offsetXRelative = offsetX / resizedW;
	const offsetYRelative = offsetY / resizedH;

	const [wRatio, hRatio] = [bbox.width / resizedW, bbox.height / resizedH];

	const x = imageSize.width * offsetXRelative;
	const y = imageSize.height * offsetYRelative;
	const w = imageSize.width * wRatio;
	const h = imageSize.height * hRatio;

	return [Math.round(x), Math.round(y), Math.round(w), Math.round(h)];
}

export const getVideoScreenShotDataURL = async (file: File) => {
	const canvas = document.createElement('canvas');
	const context = canvas.getContext('2d');
	const video = document.createElement('video');

	if (context) {
		const videoURL = await toBase64(file);

		if (videoURL && typeof videoURL === 'string') {
			video.style.opacity = '0';
			video.style.position = 'absolute';
			video.src = videoURL;
			document.body.appendChild(video);

			return new Promise<string>(resolve => {
				video.addEventListener('canplay', () => {
					const maxSize = { w: 800, h: 800 };
					const videoSize = { w: video.clientWidth, h: video.clientHeight };
					const videoRatio = video.clientHeight / video.clientWidth;

					if (videoSize.w > maxSize.w) {
						videoSize.w = maxSize.w;
						videoSize.h = videoSize.w * videoRatio;
					}
					if (videoSize.h > maxSize.h) {
						videoSize.h = maxSize.h;
						videoSize.w = videoSize.h / videoRatio;
					}

					canvas.width = videoSize.w;
					canvas.height = videoSize.h;
					context.drawImage(video, 0, 0, videoSize.w, videoSize.h);

					document.body.removeChild(video);
					resolve(canvas.toDataURL());
				});
				video.play();
			});
		}
		throw new Error('Cant get Base64 URL');
	}
	throw new Error('Cant get canvas context2D');
};

function toBase64(file: File): Promise<string | ArrayBuffer | null> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onload = () => resolve(reader.result);
		reader.onerror = error => reject(error);
	});
}
